import * as Dialog from '@radix-ui/react-dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Field, FormError, TextareaField } from '@/components/ui/field';
import { ApiError, api } from '@/lib/api';
import { formatDay } from '@/lib/dates';
import { useToast } from '@/lib/toast-context';
import { BUSINESS_TIME_ZONE } from '@shared/overtime';
import type { CorrectTimeLogInput } from '@shared/schemas/time-correction';

/*
 * Correcting a mis-punched clock — ROADMAP.md §10.3.
 *
 * **The note is required and the field says why.** business-rules.md §5 and the
 * database both insist on it, and this is the third place: a payroll figure
 * changed by hand with no reason recorded is one nobody can defend a year later.
 *
 * **The times are wall clock in Cedar City.** The inputs are a date and a time,
 * not an instant, and the server converts. Sending an ISO string would put the
 * browser's time zone into a payroll figure — an admin working from a laptop
 * still set to Pacific would move every correction by an hour.
 */

/** An instant → the date and time boxes, as Cedar City reads them. */
function toLocalParts(iso: string | null): { date: string; time: string } {
  if (iso === null) return { date: '', time: '' };

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(iso));

  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    time: `${get('hour')}:${get('minute')}`,
  };
}

export interface CorrectableShift {
  id: string;
  clockInAt: string;
  /** Null for a forgotten clock-out — the case this exists for. */
  clockOutAt: string | null;
  employeeName: string;
}

export function CorrectShiftDialog({
  shift,
  onClose,
}: {
  shift: CorrectableShift | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const opening = toLocalParts(shift?.clockInAt ?? null);
  const closing = toLocalParts(shift?.clockOutAt ?? null);

  /*
   * Keyed on the shift id so opening a different row starts clean. A fresh
   * mount is a fresh `useState` and cannot forget to reset one of five fields —
   * the same call `PhotoCaptureSheet` made and for the same reason.
   */
  return shift === null ? null : (
    <CorrectShiftForm
      key={shift.id}
      shift={shift}
      opening={opening}
      closing={closing}
      onClose={onClose}
      onSaved={() => {
        toast.success('Corrected. The original is still on the entry.');
        void queryClient.invalidateQueries({ queryKey: ['admin', 'timesheets'] });
      }}
    />
  );
}

function CorrectShiftForm({
  shift,
  opening,
  closing,
  onClose,
  onSaved,
}: {
  shift: CorrectableShift;
  opening: { date: string; time: string };
  closing: { date: string; time: string };
  onClose: () => void;
  onSaved: () => void;
}) {
  const wasOpen = shift.clockOutAt === null;

  const [clockInDate, setClockInDate] = useState(opening.date);
  const [clockInTime, setClockInTime] = useState(opening.time);
  // A forgotten clock-out has nothing to prefill, so the date defaults to the
  // day the shift began — which is right far more often than blank.
  const [clockOutDate, setClockOutDate] = useState(closing.date || opening.date);
  const [clockOutTime, setClockOutTime] = useState(closing.time);
  const [note, setNote] = useState('');

  const mutation = useMutation({
    mutationFn: (input: CorrectTimeLogInput) =>
      api.patch<unknown>(`/api/admin/time-logs/${shift.id}`, input),
  });

  const error = mutation.error instanceof ApiError ? mutation.error : null;

  function submit(event: FormEvent) {
    event.preventDefault();

    mutation.mutate(
      {
        clockInDate,
        clockInTime,
        clockOutDate: clockOutTime === '' ? null : clockOutDate,
        clockOutTime: clockOutTime === '' ? null : clockOutTime,
        note: note.trim(),
      },
      {
        onSuccess: () => {
          onSaved();
          onClose();
        },
      },
    );
  }

  return (
    <Dialog.Root
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink-900/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100vh-2rem)] w-[min(32rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg bg-white p-6 shadow-card">
          <Dialog.Title className="text-h2 text-ink-900">
            {wasOpen ? 'Close this shift' : 'Correct this shift'}
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-body text-ink-500">
            {shift.employeeName} · {formatDay(opening.date)}
            {wasOpen ? ' · still clocked in' : null}
          </Dialog.Description>

          <form onSubmit={submit} className="mt-4 space-y-4">
            {wasOpen ? (
              <p className="rounded-sm bg-honey-50 p-3 text-body text-ink-900">
                This shift was never clocked out. Enter when it actually ended — the time it was
                clocked in is kept as the original.
              </p>
            ) : null}

            <fieldset className="space-y-3">
              <legend className="text-label text-ink-900">Clocked in</legend>
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="Date"
                  type="date"
                  required
                  value={clockInDate}
                  onChange={(event) => {
                    setClockInDate(event.target.value);
                  }}
                />
                <Field
                  label="Time"
                  type="time"
                  required
                  value={clockInTime}
                  onChange={(event) => {
                    setClockInTime(event.target.value);
                  }}
                />
              </div>
            </fieldset>

            <fieldset className="space-y-3">
              <legend className="text-label text-ink-900">Clocked out</legend>
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="Date"
                  type="date"
                  required
                  value={clockOutDate}
                  onChange={(event) => {
                    setClockOutDate(event.target.value);
                  }}
                />
                <Field
                  label="Time"
                  type="time"
                  required
                  value={clockOutTime}
                  onChange={(event) => {
                    setClockOutTime(event.target.value);
                  }}
                />
              </div>
            </fieldset>

            <TextareaField
              label="Why it is being changed"
              required
              rows={3}
              hint="This is the record. It stays on the entry beside the original times."
              value={note}
              onChange={(event) => {
                setNote(event.target.value);
              }}
            />

            {/*
              The server's own words. A refused correction says which rule it
              broke — "that is 72 hours on one shift", "the shift cannot end
              before it started" — and those are more useful than "invalid".
            */}
            {error ? <FormError>{error.message}</FormError> : null}

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending || note.trim() === ''}>
                {mutation.isPending
                  ? 'Saving…'
                  : wasOpen
                    ? 'Close the shift'
                    : 'Save the correction'}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
